import { StorageError } from '../../app/errors';
import type { SettingRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `settings` — the closed-key preference table (B spec §4.2, F-5; build unit B-4).
 *
 * The table shipped in v001 with **no interactive writer on purpose**: `repos/profile.ts` offers
 * only the import's `add`-shaped `insertSetting`, because the table's one interesting property —
 * the closed key space behind §11.8's publish-or-deny archive dispositions — "depends on every
 * write being deliberate". F-5's ruling adds the writer, and this module is its storage half:
 * `putSetting` is reached through exactly one path, the `UpdateSetting` command's `putSetting`
 * op, whose handler has already validated the key against `SETTING_KEYS`. A screen cannot call
 * it — `requireTransaction` refuses outside the bus's transaction, the same guard every writer
 * in this directory carries.
 *
 * Deliberately **not** in `profile.ts`: that module is the two whole-profile operations and says
 * so; this is a per-table module like every other in this directory.
 */

/**
 * B §4.2 (F-5) — the `putSetting` op's writer: create-or-replace one `{key, value}` row.
 *
 * `put`, not `add`, and that difference from `insertSetting` is the point: the import writes
 * into a profile the same transaction just emptied (a collision there means the wipe failed and
 * must abort), while this writes a preference that usually already exists — turning the lock
 * nudge from 10 to 15 minutes *is* replacing the row.
 *
 * @throws {StorageError} outside a transaction or on a write failure.
 */
export async function putSetting(row: SettingRow): Promise<void> {
  requireTransaction('putSetting');
  try {
    await db().settings.put(row);
  } catch (cause) {
    throw new StorageError('Could not save the setting.', {
      cause,
      context: { store: TABLE.settings, key: row.key },
    });
  }
}

/**
 * One setting by key — the `UpdateSetting` snapshot read (created vs. replaced is a fact about
 * committed state, read inside the transaction like every snapshot).
 *
 * Takes `string`, not `SettingKey`: the caller may hold a key that crossed a UI boundary, and
 * answering `undefined` for an unknown one is exactly right — the handler, not this read, owns
 * the refusal.
 *
 * @throws {StorageError} on a read failure.
 */
export async function getSetting(key: string): Promise<SettingRow | undefined> {
  try {
    return await db().settings.get(key);
  } catch (cause) {
    throw new StorageError('Could not read the setting.', {
      cause,
      context: { store: TABLE.settings, key },
    });
  }
}

/**
 * Every stored setting, in key order — the More tab's `loadSettings` read. A handful of rows;
 * absence of a key means "never set", and the consuming surface supplies its own default (the
 * same contract the import leaves behind).
 *
 * @throws {StorageError} on a read failure.
 */
export async function listSettings(): Promise<SettingRow[]> {
  try {
    return await db().settings.orderBy('key').toArray();
  } catch (cause) {
    throw new StorageError('Could not read the settings.', {
      cause,
      context: { store: TABLE.settings },
    });
  }
}
